import { Project } from '../../../mocks/portfolio';

const CATEGORY_COLORS: Record<string, string> = {
  'Building & Facility Maintenance': 'bg-[#B8E2E8] text-[#05294A]',
  'Residential Remodeling': 'bg-[#7499CE]/20 text-[#05294A]',
  'Commercial Remodeling': 'bg-[#7499CE]/20 text-[#05294A]',
  'General Maintenance': 'bg-slate-100 text-slate-700',
};

const CATEGORY_ICONS: Record<string, string> = {
  'Building & Facility Maintenance': 'ri-building-2-line',
  'Residential Remodeling': 'ri-home-heart-line',
  'Commercial Remodeling': 'ri-store-2-line',
  'General Maintenance': 'ri-hammer-line',
};

const CATEGORY_SHORT: Record<string, string> = {
  'Building & Facility Maintenance': 'Building & Facility',
  'Residential Remodeling': 'Residential Remodeling',
  'Commercial Remodeling': 'Commercial Remodeling',
  'General Maintenance': 'General Maintenance',
};

interface Props {
  project: Project;
  onOpen: (project: Project) => void;
}

export default function ProjectCard({ project }: Props) {
  const colorClass = CATEGORY_COLORS[project.category] ?? 'bg-slate-100 text-slate-700';
  const iconClass = CATEGORY_ICONS[project.category] ?? 'ri-briefcase-line';
  const shortLabel = CATEGORY_SHORT[project.category] ?? project.category;

  return (
    <article
      className="group bg-white rounded-xl overflow-hidden border border-slate-200 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative overflow-hidden h-48 sm:h-52 w-full">
        <img
          src={project.mainImage}
          alt="Portfolio project"
          style={{ filter: 'blur(10px)', transform: 'scale(1.12)' }}
          className="w-full h-full object-cover object-top"
        />

        {/* Dark overlay for blurred image */}
        <div className="absolute inset-0 bg-slate-800/30 pointer-events-none" />

        {/* Coming Soon badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/55 backdrop-blur-sm rounded-full">
          <i className="ri-lock-2-line text-white text-xs" />
          <span className="text-white text-xs font-semibold tracking-wide">Coming Soon</span>
        </div>

        {/* Year badge */}
        <div className="absolute top-3 right-3 px-2 py-0.5 bg-[#05294A]/70 backdrop-blur-sm rounded-full text-white text-xs font-semibold"
          style={{ filter: 'blur(4px)', userSelect: 'none' }}
        >
          {project.year}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 md:p-5">
        {/* Category badge — visible */}
        <div className="flex items-center gap-1.5 mb-2.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
            <i className={`${iconClass} text-xs`} />
            <span className="truncate max-w-[140px] sm:max-w-none">{shortLabel}</span>
          </span>
        </div>

        {/* Title — blurred/redacted */}
        <h4
          className="text-slate-900 font-bold text-sm md:text-base leading-snug mb-2"
          style={{ filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none' }}
        >
          {project.title}
        </h4>

        {/* Location — blurred */}
        <div className="flex items-center gap-1 text-slate-400 text-xs">
          <i className="ri-map-pin-line text-[#92D7E8] text-xs" />
          <span style={{ filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none' }}>
            {project.location}
          </span>
        </div>

        {/* Stats row — blurred */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            {project.stats.slice(0, 2).map((stat) => (
              <div key={stat.label} style={{ filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none' }}>
                <p className="text-slate-900 font-bold text-xs md:text-sm">{stat.value}</p>
                <p className="text-slate-400 text-xs">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-300">
            <i className="ri-lock-2-line text-xs" />
          </div>
        </div>
      </div>
    </article>
  );
}
